import axios, { all, AxiosError } from 'axios';
import { API_GATEWAY_BASE_URL } from '@/core/global-data/constants.data';
import { clearAuthTokens } from '@/features/auth/guard/utils';
import router from '@/core/global-router';
// import { changeLanguage } from 'i18next';
import { toast } from 'sonner';

// ------------- AXIOS DEFAULTS SETTINGS -------------
// ---------------------------------------------------
const defaultURL = API_GATEWAY_BASE_URL;

axios.defaults.baseURL = defaultURL;

// ------------- GLOBAL ERROR HANDLER -------------

const handleUnauthorized = () => {
    // 1. Clear authentication tokens
    clearAuthTokens();

    // 2. Redirect to login page
    router.navigate('/login');

    // 3. Optional: Refresh the page to clear application state
    window.location.reload();
};

// ------------- CREATE AXIOS INTERCEPTORS -------------
// ----------------------------------------------------

axios.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const status = error.response?.status;
        const isUnauthorized = status === 401;

        if (isUnauthorized && !error.config?.url?.includes('/login')) {
            handleUnauthorized();
        }

        if (status === 403 && error.response?.data?.message) {
            toast.error(error.response?.data?.message);
        }

        return Promise.reject(error);
    },
);

// -------------------------------------------------------

export const endpoints = {
    auth: {
        login: '/login',
        profile: '/user',
        me: '/me',
        changePassword: '/change-password',
        forgotPassword: '/forgot-password',
        resetPassword: '/reset-password',
    },
    dashboard: {
        analytics: '/clinic-patients-dashboard',
    },

    patient: {
        create: '/clinic-patients-store',
        update: '/clinic-patients-update/',
        delete: '/clinic-patients-delete/',
        list: '/clinic-patients-paginated',
        select: '/clinic-patients-simple',
        show: '/clinic-patients-show/',
        block: '/clinic-patients-add-to-black-list/',
        dashboard: '/clinic-patients-analytics',
        all: '/clinic-patients-checkup-all/',

        logs: '/logs/user',
        logscleanup: '/logs/cleanup/',
        dashboardType: '/clinic-patients-analytics-type',
        images: '/clinic-patients-update-personal-images/',
        license: '/clinic-patients-update-license-images/',
        dashboardNewpatientss: '/clinic-patients-new-dashboard',
        typeTrack: '/clinic-patients-type-tracks/',
        typeTracks: '/customers-type-tracks',
    },

    checkup: {
        create: '/clinic-patients-checkup-store',
        update: '/clinic-patients-checkup-update',
        delete: '/clinic-patients-checkup-delete/',
        list: '/clinic-patients-checkup-paginated',
        show: '/clinic-patients-checkup-show/',
        dashboard: '/clinic-patients-checkup-analytics',
        status: '/clinic-patients-checkup-update-status'
    },

    complaint: {
        list: '/customer-complaints-paginated',
        show: '/customer-complaints-show/',
        update: '/customer-complaints-resolve/',
        // update: '/customers-complaints/',
        create: '/customer-complaints-store',
        delete: '/customers-delete/',
        resolve: '/customer-complaints-resolve/',
        dashboard: '/customer-complaints-statistics',
        open: '/customer-complaints-open'
    },

    cutomerContact: {
        list: '/clinic-patient-whatsapp-messages-paginated',
        create: '/clinic-patient-whatsapp-messages-store',
        dashboard: '/clinic-patient-whatsapp-messages-statistics',
        show: '/clinic-patient-whatsapp-messages-show/', //customer-emails-show
        update: '/customer-emails-update',
        email: '/customers-emails/',
    },

    services: {
        create: '/services-store',
        update: '/services-update',
        delete: '/services-delete/',
        list: '/services-paginated',
        show: '/services-show',
        dashboard: '/services-analytics',
        select: '/services-simple',
    },

    products: {
        list: '/products-paginated',
        create: '/products-store',
        update: '/products-update',
        delete: '/products-delete/',
        show: '/products-show',
        dashboard: '/products-analytics',
        select: '/products-simple',
    },
    finance: {
        payments: '/orders-payment-analytics',

    },
    notification: {
        list: '/notification-list',
        count: '/notification-count',
        delete: '/notification-delete/',
        read: '/notification-mark-read/',
        allRead: '/notification-mark-all-read',
        mail: '/notification-mail',
        generate: '/notification-generate',
        sendEmail: '/notification-send-emails',
    },

    orderproducts: {
        list: '/product-orders-paginated',
        create: '/product-orders-store',
        update: '/product-orders-update',
        delete: '/product-orders-delete/',
        show: '/product-orders-show',
        dashboard: '/product-orders-analytics',
        post: '/website-product-service',
        get: '/website-products',
        // select: '/products-simple',
    },
    branch: {
        create: '/branches-store',
        list: '/branches-paginated',
        show: '/branches-show/',
        update: '/branches-update/',
        delete: '/branches-delete/',
        dashboard: 'branches-analytics',
        select: 'branches-simple',
    },
    employee: {
        list: '/users-paginated',
        show: '/users-show/',
        create: '/users-store',
        update: '/users-update/',
        delete: '/users-delete/',
        dashboard: '/users-analytics',
        select: '/users-simple',
        attendance: '/users-attendance',
        salary: '/users-salary/',
        salaryList: '/users-salary-list',
        logs: '/user-logs/user/',
        usersOrdersProducts: '/users-orders-and-product-orders/',
    },
    roles: {
        list: '/role-list',
        create: '/role-create',
        update: '/role-update/',
        delete: '/role-delete/',
        show: '/role-permissions/',
    },
    permissions: {
        list: '/permission-list',
        create: '/permission-create',
        update: '/permission-update/',
        delete: '/permission-delete/',
    },
    userLimits: {
        list: '/user-limits',
    },

    subscriptions: {
        list: '/subscriptions-paginated',
        create: '/subscriptions-store',
        dashboard: '/subscriptions-analytics',
        update: '/subscriptions-update/',
        delete: '/subscriptions-delete/',
        show: '/subscriptions-show/',
        subscriptions: '/customer-subscriptions',
    },



    loan: {
        list: '/loan-request-list-paginate',
        show: '/loan-request-show/',
        update: '/loan-request-update/',
        create: '/loan-request-create',
        delete: '/loan-request-delete/',
    },
    letterRequests: {
        paginate: '/letter-request-list-paginate',
        create: '/letter-request-create',
        update: '/letter-request-update/',
        delete: '/letter-request-delete/',
    },
    extraServices: {
        list: '/service-request-list-paginate',
        show: '/service-request-show/',
        create: '/service-request-create',
        update: '/service-request-update/',
        delete: '/service-request-delete/',
    },
    employeeComplaint: {
        list: '/employee-complaint-list-paginate',
        show: '/employee-complaint-show/',
        update: '/employee-complaint-update/',
        create: '/employee-complaint-create',
        delete: '/employee-complaint-delete/',
    },
};
